import {
    Component,
    Element,
    Event,
    EventEmitter,
    h,
    Host,
    Prop,
} from '@stencil/core';
import { ThemeType } from '@uipath/portal-shell-util';

import { LanguageCode } from '../../constants/constants';
import { ChipClosedEvent } from '../../models/events/chipClosedEvent';
import { ChipType } from '../../models/types/ChipType';
import store from '../../store/store';
import { getLanguage } from '../../utils/languageHelper';
import { t } from '../../utils/localization/loc';
import { getTheme } from '../../utils/themeHelper';

@Component({
    tag: 'portal-chip',
    styleUrl: 'portal-chip.scss',
    shadow: true,
})
export class PortalChip {
    @Element() el: HTMLPortalChipElement;

    /**
    * text content for chip.
    * @memberof PortalChip
    */
    @Prop() text: string;

    /**
    * icon to display on chip
    * @memberof PortalChip
    */
    @Prop() icon?: string;

    /**
    * text content for chip.
    * @memberof PortalChip
    */
    @Prop() ariaLabel: ARIAMixin['ariaLabel'];

    /**
    * size for chip.
    * @memberof PortalChip
    */
    @Prop() size: 'normal' | 'large' = 'normal';

    /**
    * type of chip.
    * @memberof PortalChip
    */
    @Prop() type: ChipType;

    /**
     * tooltip text to show on hover
     * @memberof PortalChip
     */
    @Prop() tooltip: string;

    /**
     * show the close button for a chip
     * @memberof PortalChip
     */
    @Prop() close: boolean;

    /**
     * chip unique ID
     * @memberof PortalChip
     */
    @Prop() chipId = `chip_${chipIds++}`;

    /**
     * the option a chip is mapped to (for autocomplete)
     * @memberof PortalChip
     */
    @Prop({ attribute: 'option-data-id' }) optionId: string | null;

    /**
     * Implicit chip data
     * @memberof PortalChip
     */
    @Prop() data?: any;

    /**
     * hide chip border
     * @memberof PortalChip
     */
    @Prop() hideBorder?: boolean;

    /**
     * Override for the language ID
     * @memberof PortalChip
     */
    @Prop() language?: LanguageCode;

    /**
     * Override for the theme
     * @memberof PortalChip
     */
    @Prop() theme: ThemeType = 'autoTheme';

    /**
     * Define a custom event for chip close
     */
    @Event({
        eventName: 'chipClosed',
        composed: true,
        cancelable: false,
        bubbles: true,
    }) chipClosed: EventEmitter<ChipClosedEvent>;

    render() {
        return (
            <Host class={getTheme(store, this.theme)}>
                <portal-tooltip text={this.tooltip} disabled={!this.tooltip} position="bottom" theme={this.theme}>
                    <span
                        id={this.chipId}
                        aria-label={this.ariaLabel ?? this.text}
                        role="button"
                        data-test="portal-chip"
                        class={`chip ${this.type} ${this.size} ${this.hideBorder && 'hideBorder'}`}>
                        {this.icon && <portal-custom-icon class="chip-icon" name={this.icon} size="16px" theme={this.theme} />}
                        {this.text}
                        {
                            this.close &&
                            <portal-icon
                                class="close-btn"
                                name="close"
                                data-cy="portal-chip-close"
                                aria-label={t('remove-chip', this.language ?? getLanguage(store))}
                                size={this.getIconSize()}
                                theme={this.theme}
                                onClick={
                                    () => this.chipClosed.emit({
                                        id: this.el.id,
                                        value: this.text,
                                        data: this.data,
                                        optionId: this.optionId,
                                    })
                                } />
                        }
                    </span>
                </portal-tooltip>
            </Host>
        );
    }

    private getIconSize() {
        return this.size === 'normal' ? '10px' : '14px';
    }
}

// tracking global tooltip ids.
// todo, pradeep : Move it to Store
let chipIds = 0;
